import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArrowUp, ArrowDown, X, Plus } from "lucide-react";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

export default function AdminHero() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState([]); // ordered array of slugs
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data || [])).catch(() => {});
    api.get("/settings/hero").then((r) => setSelected(r.data?.slugs || [])).catch(() => {});
  }, []);

  const bySlug = useMemo(() => Object.fromEntries(collections.map((c) => [c.slug, c])), [collections]);
  const withImg = useMemo(() => collections.filter((c) => c.image), [collections]);
  const available = useMemo(() => withImg.filter((c) => !selected.includes(c.slug)), [withImg, selected]);

  const add = (slug) => setSelected((s) => (s.includes(slug) ? s : [...s, slug]));
  const remove = (slug) => setSelected((s) => s.filter((x) => x !== slug));
  const move = (i, dir) => setSelected((s) => {
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    const n = [...s];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const save = async () => {
    if (!adminKey) { toast.error("Enter your admin key first."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setSaving(true);
    try {
      await api.put("/settings/hero", { slugs: selected }, { headers: { "X-Admin-Key": adminKey } });
      toast.success(selected.length ? "Hero slides saved — live on the homepage." : "Cleared — homepage will auto-pick slides.");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Invalid admin key." : "Could not save. Try again.");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[900px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-hero-page">
      <Seo title="Hero Admin" path="/admin/hero" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Homepage Hero Slides</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">
        Choose exactly which Shopify collections show in the big hero banner, and drag their order. Leave empty to let the site auto-pick. Images come from each collection's Shopify image. We recommend 2–4 slides.
      </p>

      <label className="block mb-8">
        <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
        <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
          data-testid="admin-key-input" className={`${inputCls} max-w-sm`} />
      </label>

      {/* Selected slides */}
      <span className="overline text-[#1A1A1A] block mb-3">Hero Slides (in order)</span>
      {selected.length === 0 ? (
        <div className="border border-dashed border-[#E5E5DF] px-4 py-8 text-center text-sm text-[#6E6E69] mb-8" data-testid="hero-empty">
          No slides selected — the homepage will auto-pick collections (bridal, wedding, lehenga…). Add collections below.
        </div>
      ) : (
        <div className="flex flex-col gap-3 mb-8" data-testid="hero-selected">
          {selected.map((slug, i) => {
            const c = bySlug[slug];
            return (
              <div key={slug} data-testid={`hero-slide-${slug}`} className="flex items-center gap-3 border border-[#E5E5DF] p-2.5">
                <span className="w-6 text-center font-mono text-sm text-[#6E6E69]">{i + 1}</span>
                <div className="w-14 h-16 bg-[#EFEFEA] overflow-hidden flex-shrink-0">
                  {c?.image && <img src={c.image} alt={c.title} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm text-[#1A1A1A] truncate">{c?.title || slug}</div>
                  <div className="text-[0.7rem] font-mono text-[#6E6E69] truncate">{slug}</div>
                </div>
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`hero-up-${slug}`}
                  className="p-2 hover:text-[#B84A3B] disabled:opacity-30" aria-label="Move up"><ArrowUp size={16} /></button>
                <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} data-testid={`hero-down-${slug}`}
                  className="p-2 hover:text-[#B84A3B] disabled:opacity-30" aria-label="Move down"><ArrowDown size={16} /></button>
                <button onClick={() => remove(slug)} data-testid={`hero-remove-${slug}`}
                  className="p-2 hover:text-[#B84A3B]" aria-label="Remove"><X size={16} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Available collections */}
      <span className="overline text-[#1A1A1A] block mb-3">Add a Collection</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-10" data-testid="hero-available">
        {available.map((c) => (
          <button key={c.slug} onClick={() => add(c.slug)} data-testid={`hero-add-${c.slug}`}
            className="group relative text-left border border-[#E5E5DF] overflow-hidden hover:border-[#1A1A1A] transition-colors">
            <div className="aspect-[4/5] bg-[#EFEFEA] overflow-hidden">
              <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-2.5 flex items-center justify-between text-[#FFFFFF]">
              <span className="font-body text-xs truncate">{c.title}</span>
              <Plus size={16} className="flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="hero-save-button"
        className="w-full sm:w-auto bg-[#1A1A1A] text-[#FFFFFF] px-10 py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60">
        {saving ? "Saving…" : "Save & Publish"}
      </button>
    </main>
  );
}
